// src/components/ModalLoginSponsor.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ModalLoginSponsorProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (token: string, sponsor: any) => void;
}

function ModalLoginSponsor({ isOpen, onClose, onLoginSuccess }: ModalLoginSponsorProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [etape, setEtape] = useState<'identifiants' | 'code2fa' | 'bloque'>('identifiants');
  const [userId2fa, setUserId2fa] = useState<number | null>(null);
  const [code2fa, setCode2fa] = useState('');
  const [loading2fa, setLoading2fa] = useState(false);
  const [codeDeblocage, setCodeDeblocage] = useState('');
  const [loadingDeblocage, setLoadingDeblocage] = useState(false);
  const [loadingRenvoi, setLoadingRenvoi] = useState(false);
  const [messageDeblocage, setMessageDeblocage] = useState('');

  // ── INSCRIPTION (redirige vers la page d'inscription) ──────────────────
  const handleRegisterClick = () => {
    onClose();
    navigate('/commanditaire/inscription');
  };

  // ── CONNEXION ────────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/sponsors/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, mot_de_passe: motDePasse })
      });

      const data = await response.json();

      if (response.status === 403 && data.blocked === true) {
        setEtape('bloque');
        setError('');
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Email ou mot de passe incorrect');
      }

      if (data.requires2FA) {
        setUserId2fa(data.userId);
        setEtape('code2fa');
        return;
      }

      // Le cookie httpOnly est déjà posé par le serveur — on ne stocke plus
      // le JWT ici, seulement les infos d'affichage non sensibles.
      localStorage.setItem('sponsor', JSON.stringify(data.sponsor));

      onLoginSuccess(data.token, data.sponsor);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── VÉRIFICATION DU CODE F2A ─────────────────────────────────────────────
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading2fa(true);
    setError('');

    try {
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: userId2fa, code: code2fa, userType: 'commanditaire' }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Code invalide ou expiré');
      }

      // Le cookie httpOnly est déjà posé par le serveur (/verify-2fa dans
      // authStudio.js, déjà migré) — on ne stocke plus le JWT ici.
      localStorage.setItem('sponsor', JSON.stringify(data.user));

      onLoginSuccess(data.token, data.user);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading2fa(false);
    }
  };

  // ── DÉBLOCAGE DU COMPTE (après 5 tentatives échouées) ────────────────────
  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingDeblocage(true);
    setError('');
    setMessageDeblocage('');

    try {
      const response = await fetch('/api/auth/unlock-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, userType: 'commanditaire', code: codeDeblocage }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Code invalide ou expiré');
      }
      setMessageDeblocage('Compte débloqué. Vous pouvez vous reconnecter.');
      setCodeDeblocage('');
      setTimeout(() => { setEtape('identifiants'); setMessageDeblocage(''); }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingDeblocage(false);
    }
  };

  const handleResendUnlockCode = async () => {
    setLoadingRenvoi(true);
    setError('');
    setMessageDeblocage('');
    try {
      const response = await fetch('/api/auth/resend-unlock-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, userType: 'commanditaire' }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Erreur lors de l'envoi du code");
      }
      setMessageDeblocage('Un nouveau code a été envoyé par courriel.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingRenvoi(false);
    }
  };

  if (!isOpen) return null;


  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '440px',
          padding: '32px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
        }}
      >
        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>
              ⭐ Connexion
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#666' }}>
              Commanditaire existant
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ✕
          </button>
        </div>

        {/* Formulaire */}
        {etape === 'identifiants' ? (
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@mamarque.com"
              required
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>
              Mot de passe
            </label>
            <input
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              placeholder="••••••••"
              required
              minLength={8}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 14px',
              background: '#fee2e2',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              ❌ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              border: 'none',
              borderRadius: '10px',
              color: '#000',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '⏳...' : '🚀 Se connecter'}
          </button>
        </form>
        ) : etape === 'code2fa' ? (
        <form onSubmit={handleVerifyCode}>
          <p style={{ fontSize: '14px', color: '#333', margin: '0 0 16px', lineHeight: 1.6 }}>
            Un code de vérification vous a été envoyé par courriel. Entrez-le ci-dessous.
          </p>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>
              Code de vérification
            </label>
            <input
              type="text"
              value={code2fa}
              onChange={(e) => setCode2fa(e.target.value)}
              placeholder="123456"
              required
              autoFocus
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '18px',
                letterSpacing: '4px',
                textAlign: 'center',
                fontFamily: 'monospace',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 14px',
              background: '#fee2e2',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              ❌ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading2fa}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              border: 'none',
              borderRadius: '10px',
              color: '#000',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: loading2fa ? 0.7 : 1,
            }}
          >
            {loading2fa ? '⏳...' : '✅ Vérifier le code'}
          </button>
          <button
            type="button"
            onClick={() => { setEtape('identifiants'); setCode2fa(''); setError(''); }}
            style={{ width: '100%', marginTop: '10px', padding: '8px', background: 'transparent', border: 'none', color: '#666', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            ← Retour
          </button>
        </form>
        ) : (
        <form onSubmit={handleUnlock}>
          <div style={{
            padding: '10px 14px',
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            color: '#92400e',
            fontSize: '13px',
            marginBottom: '16px',
            lineHeight: 1.6,
          }}>
            🔒 Compte bloqué pour cause de trop nombreuses tentatives. Un code de déblocage a été envoyé par courriel à <strong>{email}</strong>.
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>
              Code de déblocage
            </label>
            <input
              type="text"
              value={codeDeblocage}
              onChange={(e) => setCodeDeblocage(e.target.value)}
              placeholder="123456"
              required
              autoFocus
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '18px',
                letterSpacing: '4px',
                textAlign: 'center',
                fontFamily: 'monospace',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 14px',
              background: '#fee2e2',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              ❌ {error}
            </div>
          )}

          {messageDeblocage && (
            <div style={{
              padding: '10px 14px',
              background: '#dcfce7',
              borderRadius: '8px',
              color: '#16a34a',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              ✅ {messageDeblocage}
            </div>
          )}

          <button
            type="submit"
            disabled={loadingDeblocage}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              border: 'none',
              borderRadius: '10px',
              color: '#000',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: loadingDeblocage ? 0.7 : 1,
            }}
          >
            {loadingDeblocage ? '⏳...' : '🔓 Débloquer mon compte'}
          </button>
          <button
            type="button"
            onClick={handleResendUnlockCode}
            disabled={loadingRenvoi}
            style={{ width: '100%', marginTop: '10px', padding: '8px', background: 'transparent', border: 'none', color: '#f59e0b', fontSize: '13px', fontWeight: 600, cursor: loadingRenvoi ? 'not-allowed' : 'pointer', textDecoration: 'underline' }}
          >
            {loadingRenvoi ? '⏳ Envoi...' : 'Renvoyer le code'}
          </button>
          <button
            type="button"
            onClick={() => { setEtape('identifiants'); setCodeDeblocage(''); setError(''); setMessageDeblocage(''); }}
            style={{ width: '100%', marginTop: '6px', padding: '8px', background: 'transparent', border: 'none', color: '#666', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            ← Retour
          </button>
        </form>
        )}

        {/* Basculer vers l'inscription */}
        {etape === 'identifiants' && (
        <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
          Pas encore de compte commanditaire ?{' '}
          <button
            onClick={handleRegisterClick}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#f59e0b',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            S'inscrire
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

export default ModalLoginSponsor;